import { Component, Input, Output, EventEmitter, forwardRef, OnDestroy } from '@angular/core';
import { TemplateNode } from '../certification-template-editor.component';
import { v4 as uuidv4 } from 'uuid';
import { CdkDrag, CdkDragDrop, moveItemInArray, transferArrayItem, DragDropModule } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TemplateDragStateService } from '../template-drag-state.service';

@Component({
  selector: 'app-template-node-editor',
  templateUrl: './template-node-editor.component.html',
  styleUrls: ['./template-node-editor.component.css'],
  standalone: true,
  imports: [
    CommonModule, 
    MatIconModule, 
    MatFormFieldModule, 
    MatInputModule, 
    FormsModule, 
    MatButtonModule, 
    MatSelectModule, 
    DragDropModule,
    forwardRef(() => TemplateNodeEditorComponent)
  ]
})
export class TemplateNodeEditorComponent implements OnDestroy {
  @Input() node: TemplateNode;
  @Input() parentNodes: TemplateNode[];
  @Output() nodeChange = new EventEmitter<void>();

  private expandTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(public dragState: TemplateDragStateService) { }

  get childNodes(): TemplateNode[] {
    if (!this.node.children) this.node.children = [];
    return this.node.children;
  }

  toggleExpand() {
    this.node.expanded = !this.node.expanded;
    this.nodeChange.emit();
  }

  addChild() {
    this.childNodes.push({
      id: uuidv4(),
      name: '',
      type: 'string',
      children: [],
      expanded: true
    });
    this.node.type = 'object';
    this.node.expanded = true;
    this.nodeChange.emit();
  }

  isGroup(): boolean {
    return this.node.type === 'object' || this.childNodes.length > 0;
  }

  canDropIntoNode = (drag: CdkDrag<TemplateNode>): boolean => {
    return this.dragState.canMoveInto(this.node, drag.data);
  };

  onDropTargetEnter(): void {
    if (!this.dragState.enterNode(this.node)) return;
    this.clearExpandTimer();
    if (this.isGroup() && !this.node.expanded) {
      this.expandTimer = setTimeout(() => {
        this.node.expanded = true;
        this.nodeChange.emit();
      }, 450);
    }
  }

  onDropTargetExit(): void {
    this.dragState.leaveNode(this.node);
    this.clearExpandTimer();
  }

  deleteNode() {
    const idx = this.parentNodes.indexOf(this.node);
    if (idx !== -1) {
      this.parentNodes.splice(idx, 1);
      this.nodeChange.emit();
    }
  }

  onSelfChange() {
    this.nodeChange.emit();
  }

  drop(event: CdkDragDrop<TemplateNode[]>) {
    if (this.dragState.applyInsideDrop(event)) {
      this.nodeChange.emit();
      return;
    }

    if (event.previousContainer.data === event.container.data) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
    this.dragState.reset();
    this.nodeChange.emit();
  }

  ngOnDestroy(): void {
    this.clearExpandTimer();
  }

  private clearExpandTimer(): void {
    if (this.expandTimer) {
      clearTimeout(this.expandTimer);
      this.expandTimer = null;
    }
  }

}
